import { DEFAULT_BRAND_KEY, type BrandKey } from '../../config/brands';
import { BRAND_PROFILES } from '../../config/brandProfiles';
import type { StatementModel, WorkbenchState } from './useStatementModel';
import { monthLabel } from './useStatementModel';

/**
 * WYSIWYG CSV export of the composed statement — exactly what the workbench
 * shows (row order, overrides, add-backs, adjustments, excluded rows) plus a
 * header block recording the rules. The export is the frozen paid-record.
 */

const esc = (v: string | number): string => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const money = (cents: number): string => (cents / 100).toFixed(2);
const pct = (p: number): string => `${(p * 100).toFixed(2).replace(/\.?0+$/, '')}%`;

export function buildStatementCsv(
  model: StatementModel,
  state: WorkbenchState,
  month: string,
  generatedAt: Date = new Date(),
  brand: BrandKey = DEFAULT_BRAND_KEY,
): string {
  const lines: string[] = [];
  const row = (...cells: Array<string | number>) => lines.push(cells.map(esc).join(','));

  row('Revenue Share Statement', monthLabel(month));
  // A statement covers one brand's programs. Recorded because the file outlives the screen
  // that showed which brand was selected.
  row('Brand', BRAND_PROFILES[brand].name);
  row('Generated at', generatedAt.toISOString());
  row('Share %', pct(state.sharePct));
  row(
    'Rules applied',
    [
      'Cash basis',
      'Tuition-first',
      'Discounts netted',
      state.excludeBonus ? 'BHCS Cash bonus excluded' : 'BHCS Cash bonus INCLUDED (added back)',
      state.excludeFeesMaterials ? 'Fees & materials excluded' : 'Fees & materials INCLUDED (added back)',
    ].join('; '),
  );
  const overridden = model.programs.filter((p) => p.overridden);
  if (overridden.length > 0) {
    row('Share overrides', overridden.map((p) => `${p.programName}: ${pct(p.sharePct)}`).join('; '));
  }
  row();
  row('Program', 'Payments', 'Refunds', 'Gross tuition', 'Bonus excluded', 'Rent base', 'Share', 'Rent');
  for (const p of model.programs) {
    row(
      p.programName,
      p.nPayments,
      p.nRefunds,
      money(p.grossTuitionCents),
      money(p.bonusCents),
      money(p.baseCents),
      pct(p.sharePct) + (p.overridden ? ' *' : ''),
      money(p.rentCents),
    );
  }
  for (const a of model.addBacks) {
    row(a.label, '', '', '', '', money(a.amountCents), pct(state.sharePct), money(a.rentCents));
  }
  for (const a of model.adjustments) {
    row(`Adjustment: ${a.label}`, '', '', '', '', money(a.amountCents), pct(state.sharePct), money(a.rentCents));
  }
  row(
    'TOTAL',
    model.totals.nPayments,
    model.totals.nRefunds,
    money(model.totals.grossTuitionCents),
    money(model.totals.bonusExcludedCents),
    money(model.totals.baseCents),
    '',
    money(model.totals.rentCents),
  );
  if (model.excludedPrograms.length > 0) {
    row();
    row('Excluded rows (not in totals)');
    for (const p of model.excludedPrograms) {
      row(p.programName, p.nPayments, p.nRefunds, money(p.grossTuitionCents), money(p.bonusCents), money(p.baseCents), '', '');
    }
  }
  return lines.join('\n');
}
